import { useMemo } from 'react';

interface ParsedActionData {
  member?: { id: string };
  checkItem?: { id: string };
  checklist?: { id: string };
  listBefore?: { id: string };
  listAfter?: { id: string };
  attachment?: { id: string };
  board?: { id: string };
}

type ActionBase = {
  id: string;
  display: { translationKey: string };
  date: string;
  idMemberCreator: string;
  data: string;
};

const dup = (condition: boolean) => [condition, condition];

const cancelingPairs = [
  {
    a: 'action_member_left_card',
    b: 'action_member_joined_card',
    cancel: (a: ParsedActionData, b: ParsedActionData) =>
      dup(a.member?.id === b.member?.id),
  },
  {
    a: 'action_removed_member_from_card',
    b: 'action_added_member_to_card',
    cancel: (a: ParsedActionData, b: ParsedActionData) =>
      dup(a.member?.id === b.member?.id),
  },
  {
    a: 'action_marked_checkitem_incomplete',
    b: 'action_completed_checkitem',
    cancel: (a: ParsedActionData, b: ParsedActionData) =>
      dup(a.checkItem?.id === b.checkItem?.id),
  },
  {
    a: 'action_remove_checklist_from_card',
    b: 'action_add_checklist_to_card',
    cancel: (a: ParsedActionData, b: ParsedActionData) =>
      dup(a.checklist?.id === b.checklist?.id),
  },
  {
    a: 'action_move_card_from_list_to_list',
    b: 'action_move_card_from_list_to_list',
    cancel: (a: ParsedActionData, b: ParsedActionData) =>
      dup(a.listAfter?.id === b.listBefore?.id),
  },
  {
    a: 'action_delete_attachment_from_card',
    b: 'action_add_attachment_to_card',
    cancel: (a: ParsedActionData, b: ParsedActionData) =>
      dup(a.attachment?.id === b.attachment?.id),
  },
  {
    a: 'action_changed_board_background',
    b: 'action_changed_board_background',
    cancel: (a: ParsedActionData, b: ParsedActionData) =>
      dup(a.board?.id === b.board?.id),
  },
];

/*
 * This hook filters out actions that are considered to be canceling each other.  The list includes
 * if a member leaves and joins a card, if a check item is marked incomplete and completed, if a
 * checklist is removed and added, if a card is moved from one list to another, and if an attachment
 * is deleted and added.  The actions must be performed by the same member and within 5 minutes of
 * each other to be considered canceling.
 */
export function useCanceledPairs<T extends ActionBase>(actions: T[]): T[] {
  return useMemo(() => {
    if (actions.length < 2) {
      return actions;
    }

    const canceled = new Set();

    const parsedActions: Record<string, ParsedActionData> = {};

    actions.forEach((action, i) => {
      if (i === actions.length - 1) {
        return;
      }

      const nextAction = actions[i + 1];

      parsedActions[action.id] =
        parsedActions[action.id] || JSON.parse(action.data);
      parsedActions[nextAction.id] =
        parsedActions[nextAction.id] || JSON.parse(nextAction.data);

      const actionData = parsedActions[action.id];
      const nextActionData = parsedActions[nextAction.id];

      if (
        action.idMemberCreator === nextAction.idMemberCreator &&
        !canceled.has(action.id) &&
        !canceled.has(nextAction.id)
      ) {
        const timeDiff =
          Math.abs(
            new Date(action.date).getTime() -
              new Date(nextAction.date).getTime(),
          ) /
          (1000 * 60);

        if (timeDiff <= 5) {
          for (const pair of cancelingPairs) {
            if (
              action.display.translationKey === pair.a &&
              nextAction.display.translationKey === pair.b
            ) {
              const [cancelA, cancelB] = pair.cancel(
                actionData,
                nextActionData,
              );

              if (cancelA) {
                canceled.add(action.id);
              }

              if (cancelB) {
                canceled.add(nextAction.id);
              }

              break;
            }
          }
        }
      }
    });

    return actions.filter((action) => !canceled.has(action.id));
  }, [actions]);
}
